import React, { FC, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { Grid } from '@mui/material'
import { IBankAccount, PortfolioCurrenciesEnum } from '@sparling/financial-common'
import { InputSelect } from '../../../../components/InputSelect'
import { InputField } from '../../../../components/InputField'
import { createEnum } from '../../../../utils/createEnum'
import { Modal } from '../../../../components/Modal'

interface Props {
  isOpen: boolean
  modalClose: () => void
  fields: IBankAccount
}

export const ViewBankAccountModal: FC<Props> = ({ isOpen, fields, modalClose }) => {
  const [formValues, setFormValues] = useState<IBankAccount>(fields || {})

  const formMethods = useForm<IBankAccount>({
    mode: 'all',
    reValidateMode: 'onChange',
    shouldFocusError: true,
  })

  const { getValues, setValue, reset } = formMethods
  const shouldFieldsBeDisabled = true

  useEffect(() => {
    if (!!fields && Object.keys(fields).length > 0) {
      setFormValues((prev) => {
        if (Object.keys(prev).length === 0) {
          return fields
        }

        return prev
      })
    }
  }, [fields])

  useEffect(() => {
    reset()
    Object.entries(formValues).forEach(([key, value]) => {
      setValue(key as keyof IBankAccount, value)
    })
  }, [formValues, getValues, reset, setValue])

  return (
    <Modal
      title={'Bank Account Details'}
      isOpen={isOpen}
      modalClose={modalClose}
      onCancel={modalClose}
      cancelTitle={'Close'}
      width={680}
      submitButtonProps={{
        visible: false,
      }}
    >
      <FormProvider {...formMethods}>
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6} container direction="column">
            <InputSelect
              key="currency"
              labelName="Currency"
              inputName="currency"
              placeholder="Please Select"
              enums={createEnum(PortfolioCurrenciesEnum)}
              width={'100%'}
              mb={20}
              defaultValue={formValues?.currency}
              disable={shouldFieldsBeDisabled}
            />
            <InputField
              key="bankName"
              labelName="Bank Name"
              inputName="bankName"
              inputType="text"
              width={'100%'}
              defaultValue={formValues?.bankName}
              disabled={shouldFieldsBeDisabled}
            />
            <InputField
              key="bankSwift"
              labelName="Bank SWIFT/BIC"
              inputName="bankSwift"
              inputType="text"
              width={'100%'}
              defaultValue={formValues?.bankSwift}
              disabled={shouldFieldsBeDisabled}
            />
            <InputField
              key="bankAccountNumber"
              labelName="Bank Account Number"
              inputName="bankAccountNumber"
              inputType="text"
              width={'100%'}
              defaultValue={formValues?.bankAccountNumber}
              disabled={shouldFieldsBeDisabled}
            />
            <InputField
              key="bankAddress"
              labelName="Bank Address"
              inputName="bankAddress"
              inputType="text"
              width={'100%'}
              defaultValue={formValues?.bankAddress}
              disabled={shouldFieldsBeDisabled}
            />
          </Grid>

          <Grid item xs={12} sm={6} container direction="column">
            <InputField
              key="beneficiaryName"
              labelName="Beneficiary Name"
              inputName="beneficiaryName"
              inputType="text"
              width={'100%'}
              defaultValue={formValues?.beneficiaryName}
              disabled={shouldFieldsBeDisabled}
            />
            <InputField
              key="intermediaryBankName"
              labelName="Intermediary Bank Name"
              inputName="intermediaryBankName"
              inputType="text"
              width={'100%'}
              defaultValue={formValues?.intermediaryBankName}
              disabled={shouldFieldsBeDisabled}
            />
            <InputField
              key="intermediaryBankSwift"
              labelName="Intermediary Bank SWIFT/BIC"
              inputName="intermediaryBankSwift"
              inputType="text"
              width={'100%'}
              defaultValue={formValues?.intermediaryBankSwift}
              disabled={shouldFieldsBeDisabled}
            />
            <InputField
              key="intermediaryBankAddress"
              labelName="Intermediary Bank Address"
              inputName="intermediaryBankAddress"
              inputType="text"
              width={'100%'}
              defaultValue={formValues?.intermediaryBankAddress}
              disabled={shouldFieldsBeDisabled}
            />
            <InputField
              key="referenceToInclude"
              labelName="Reference to Include"
              inputName="referenceToInclude"
              inputType="text"
              width={'100%'}
              defaultValue={formValues?.referenceToInclude}
              disabled={shouldFieldsBeDisabled}
            />
          </Grid>
        </Grid>
      </FormProvider>
    </Modal>
  )
}
